import React, { useState, useMemo, useRef, useEffect } from "react";
import { Button, Modal, Radio, Select, message } from "antd";
import { PhoneInput } from "react-international-phone";
import ProfilePictureUpload from "../../ProfilePictureUpload/ProfilePictureUpload";
import CustomTypeAhead from "../../Commons/Forms/CustomTypeAhead";
import TextField from "../../Commons/Forms/TextField";
import DateField from "../../Commons/Forms/DateField";
import SelectField from "../../Commons/Forms/SelectField";
import countryList from "react-select-country-list";
import "react-international-phone/style.css";
import { getAge, getToken, getUser } from "../../Helpers/Utilities/Common";
import {
  nationalities,
  phCitiesList,
  phProvincesList,
} from "../../../Helpers/Utils/Common";
import { getWithParamsQRAPICall } from "../../Helpers/API/axiosMethodCall";
import { CameraOutlined, SettingOutlined, PlusOutlined, LoadingOutlined } from "@ant-design/icons";
import Webcam from "react-webcam";
import { compressBase64Image, compressImage } from "../../Helpers/Utilities/imageCompression";
import { Upload as AntDUpload } from "antd";

const AddPatientForm = ({
  type,
  addPatient,
  userData,
  loading,
  isError,
  isChecked,
  handleCheckboxChange,
  handleAddChange,
  navigatePatientProfile,
  handleSaveImage,
  setAddPatient,
  handleValueChange,
  handleAddPhoneChange,
}) => {
  const [showPhilhealthPatientForm, setShowPhilhealthPatientForm] =
    useState(false);
  const [showPhilHealth, setShowPhilHealth] = useState(!!addPatient.ph_check);
  const [isMiddleNameNA, setIsMiddleNameNA] = useState(false);
  const [birthdateFutureError, setBirthdateFutureError] = useState(false);
  const todayISO = new Date().toISOString().split("T")[0];
  const isPWD =
    addPatient?.is_pwd === "1" ||
    addPatient?.is_pwd === 1 ||
    addPatient?.is_pwd === true;
  const needsSeniorPwdEmergencyContact =
    getAge(addPatient.birthdate) >= 60 || isPWD;
  const countryOptions = useMemo(
    () =>
      countryList()
        .getData()
        .map((c) => ({
          value: c?.label?.toUpperCase(),
          label: c.label?.toUpperCase(),
        })),
    []
  );
  const [isCameraModalVisible, setIsCameraModalVisible] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const [availableCameras, setAvailableCameras] = useState([]);
  const [selectedCamera, setSelectedCamera] = useState(null);
  const webcamRef = useRef(null);
  const [patientPhoto, setPatientPhoto] = useState(null);
  const [phProvinces, setPhProvinces] = useState([]);
  const [phCities, setPhCities] = useState([]);
  const [phBarangays, setPhBarangays] = useState([]);
  const [livenessLoading, setLivenessLoading] = useState(false);
  const [livenessPreview, setLivenessPreview] = useState(null);

  const handleLivenessUpload = async (info) => {
    setLivenessLoading(true);
    try {
      const originalSizeKB = (info.file.size / 1024).toFixed(2);
      const compressedFile = await compressImage(info.file, 500, 0.8);
      const compressedSizeKB = (compressedFile.size / 1024).toFixed(2);
      
      console.log(`📸 Liveness Image Compression Result:`);
      console.log(`   Original size: ${originalSizeKB} KB`);
      console.log(`   Compressed size: ${compressedSizeKB} KB`);
      
      setAddPatient((prev) => ({
        ...prev,
        liveness_check_screenshot: compressedFile,
      }));

      const reader = new FileReader();
      reader.onload = (e) => {
        setLivenessPreview(e.target.result);
        setLivenessLoading(false);
      };
      reader.readAsDataURL(compressedFile);
    } catch (error) {
      console.error("Liveness image compression failed:", error);
      message.error("Failed to process liveness image");
      setLivenessLoading(false);
    }
  };

  const fetchPhAreas = async (filters) => {
    try {
      const response = await getWithParamsQRAPICall(
        process.env.REACT_APP_LINK_CSR + "ph_areas/get",
        {
          origin: "emr",
          province: filters?.province || "",
          municipality: filters?.municipality || "",
        }
      );

      const payload = response && response.data;
      if (!payload) {
        return [];
      }

      if (Array.isArray(payload)) {
        return payload;
      }

      if (Array.isArray(payload.data)) {
        return payload.data;
      }

      return [];
    } catch (error) {
      console.error("Error fetching PH areas:", error);
      return [];
    }
  };

  useEffect(() => {
    setShowPhilHealth(!!addPatient.ph_check);
  }, [addPatient.ph_check]);

  useEffect(() => {
    const loadProvinces = async () => {
      if (addPatient?.country?.toUpperCase() !== "PHILIPPINES") {
        setPhProvinces([]);
        setPhCities([]);
        setPhBarangays([]);
        return;
      }

      const rows = await fetchPhAreas({});
      if (!rows || !rows.length) {
        setPhProvinces([]);
        return;
      }

      const uniqueProvinces = {};
      rows.forEach((item) => {
        const provinceName = item.province && item.province.toUpperCase();
        if (provinceName && !uniqueProvinces[provinceName]) {
          uniqueProvinces[provinceName] = provinceName;
        }
      });

      const provinces = Object.keys(uniqueProvinces).map((name, index) => ({
        key: index,
        label: name,
        value: name,
      }));

      setPhProvinces(provinces);
    };

    loadProvinces();
  }, [addPatient?.country]);

  // Handle autofilled province and city to load their respective dropdowns
  useEffect(() => {
    const loadCitiesAndBarangays = async () => {
      if (addPatient?.country?.toUpperCase() === "PHILIPPINES" && addPatient?.province) {
        const cityRows = await fetchPhAreas({ province: addPatient.province });
        if (cityRows && cityRows.length) {
          const uniqueCities = {};
          cityRows.forEach((item) => {
            const cityName = item.municipality && item.municipality.toUpperCase();
            if (cityName && !uniqueCities[cityName]) {
              uniqueCities[cityName] = cityName;
            }
          });
          setPhCities(Object.keys(uniqueCities).map((name, index) => ({
            key: index,
            label: name,
            value: name,
          })));

          if (addPatient?.city) {
            const cityUpper = addPatient.city.toUpperCase();
            const rowsForCity = cityRows.filter(
              (item) => item.municipality && item.municipality.toUpperCase() === cityUpper
            );
            const barangaySource = rowsForCity.length ? rowsForCity : cityRows;
            setPhBarangays(barangaySource
              .filter((item) => item.barangay && item.barangay.trim() !== "")
              .map((item, index) => ({
                ...item,
                key: index,
                label: item.barangay,
                value: item.barangay,
              })));
          }
        }
      }
    };
    loadCitiesAndBarangays();
  }, [addPatient?.province, addPatient?.city]);

  // Get available cameras
  useEffect(() => {
    const getCameras = async () => {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoDevices = devices.filter(
          (device) => device.kind === "videoinput"
        );
        setAvailableCameras(videoDevices);

        // Try to find external webcam (often has label containing "USB" or "External")
        const externalWebcam = videoDevices.find(
          (device) =>
            device.label.toLowerCase().includes("usb") ||
            device.label.toLowerCase().includes("external") ||
            device.label.toLowerCase().includes("logitech") || // Common webcam brands
            device.label.toLowerCase().includes("c920") ||
            !device.label.toLowerCase().includes("facetime") // Exclude built-in MacBook camera
        );

        if (externalWebcam) {
          setSelectedCamera(externalWebcam.deviceId);
        } else if (videoDevices.length > 0) {
          setSelectedCamera(videoDevices[0].deviceId);
        }
      } catch (error) {
        console.error("Error getting cameras:", error);
      }
    };

    if (isCameraModalVisible) {
      getCameras();
    }
  }, [isCameraModalVisible]);

  const capturePhoto = () => {
    const imageSrc = webcamRef.current.getScreenshot();
    setCapturedImage(imageSrc);
  };

  const retakePhoto = () => {
    setCapturedImage(null);
  };

  const usePhoto = async () => {
    if (capturedImage) {
      try {
        // Compress the captured image to max 500KB
        const originalSizeKB = (capturedImage.length * 0.75 / 1024).toFixed(2); // Approximate base64 size
        const { base64, blob } = await compressBase64Image(capturedImage, 500, 0.8);
        const compressedSizeKB = (blob.size / 1024).toFixed(2);
        
        console.log(` camera Image Compression Result:`);
        console.log(`   Original size (approx): ${originalSizeKB} KB`);
        console.log(`   Compressed size: ${compressedSizeKB} KB`);
        
        const file = new File([blob], "patient-photo.jpg", {
          type: "image/jpeg",
        });
        handleSaveImage(file);
        setPatientPhoto(base64);
        setCapturedImage(base64);
      } catch (error) {
        console.error("Image compression failed:", error);
        message.error("Failed to process captured image");
      }
    }
    setIsCameraModalVisible(false);
  };

  const videoConstraints = {
    width: 1280,
    height: 810,
    facingMode: "user",
    deviceId: selectedCamera ? { exact: selectedCamera } : undefined,
  };

  const handleCameraChange = (deviceId) => {
    setSelectedCamera(deviceId);
  };

  const handleProvinceChange = async (value) => {
    handleAddChange({
      target: { name: "province", value },
    });

    setAddPatient((prev) => ({
      ...prev,
      city: "",
      barangay: "",
      zip_code: "",
    }));

    if (addPatient?.country?.toUpperCase() !== "PHILIPPINES") {
      return;
    }

    const rows = await fetchPhAreas({ province: value });
    if (!rows || !rows.length) {
      setPhCities([]);
      setPhBarangays([]);
      return;
    }

    const uniqueCities = {};
    rows.forEach((item) => {
      const cityName = item.municipality && item.municipality.toUpperCase();
      if (cityName && !uniqueCities[cityName]) {
        uniqueCities[cityName] = cityName;
      }
    });

    const cities = Object.keys(uniqueCities).map((name, index) => ({
      key: index,
      label: name,
      value: name,
    }));

    setPhCities(cities);
    setPhBarangays([]);
  };

  const handleCityChange = async (value) => {
    handleAddChange({
      target: { name: "city", value },
    });

    setAddPatient((prev) => ({
      ...prev,
      barangay: "",
      zip_code: "",
    }));

    if (addPatient?.country?.toUpperCase() !== "PHILIPPINES") {
      return;
    }

    const rows = await fetchPhAreas({ province: addPatient.province });
    if (!rows || !rows.length) {
      setPhBarangays([]);
      return;
    }

    const cityUpper = value && value.toUpperCase();

    const rowsForCity = rows.filter(
      (item) =>
        item.municipality &&
        item.municipality.toUpperCase() === cityUpper
    );

    const barangaySource = rowsForCity.length ? rowsForCity : rows;

    const barangays = barangaySource
      .filter((item) => item.barangay && item.barangay.trim() !== "")
      .map((item, index) => ({
        ...item,
        key: index,
        label: item.barangay,
        value: item.barangay,
      }));

    setPhBarangays(barangays);

    const firstZipRow =
      rowsForCity.find(
        (item) =>
          item.zip_code !== undefined &&
          item.zip_code !== null &&
          String(item.zip_code).trim() !== ""
      ) ||
      rows.find(
        (item) =>
          item.zip_code !== undefined &&
          item.zip_code !== null &&
          String(item.zip_code).trim() !== ""
      );

    if (firstZipRow && firstZipRow.zip_code) {
      setAddPatient((prev) => ({
        ...prev,
        zip_code: String(firstZipRow.zip_code),
      }));
    }
  };

  const handleBarangayInputChange = async (e) => {
    handleAddChange(e);

    if (addPatient?.country?.toUpperCase() !== "PHILIPPINES" || !addPatient.city) {
      return;
    }

    const barangayName = e.target.value && e.target.value.toUpperCase();
    if (!barangayName) {
      return;
    }

    const rows = await fetchPhAreas({ municipality: addPatient.city });
    if (!rows || !rows.length) {
      return;
    }

    const match = rows.find(
      (item) =>
        item.barangay && item.barangay.toUpperCase() === barangayName
    );

    if (match && match.zip_code) {
      setAddPatient((prev) => ({
        ...prev,
        zip_code: String(match.zip_code),
      }));
    }
  };

  const handleBarangaySelectChange = (value) => {
    handleAddChange({
      target: { name: "barangay", value },
    });

    const selected = phBarangays.find(
      (item) => item.value === value || item.barangay === value
    );

    if (selected && selected.zip_code) {
      setAddPatient((prev) => ({
        ...prev,
        zip_code: String(selected.zip_code),
      }));
    }
  };

  return (
    <>
      <style>
        {`
          .liveness-uploader-wrapper .ant-upload.ant-upload-select {
            width: 100% !important;
            height: 80px !important;
            max-width: 280px;
            border-radius: 12px !important;
            background-color: #fdfdfd !important;
            border: 1px dashed #d9d9d9 !important;
          }
          .liveness-uploader-wrapper .ant-upload-select:hover {
            border-color: #40a9ff !important;
          }
          .liveness-note {
            font-size: 14px;
            color: #333;
            line-height: 1.5;
            margin-bottom: 15px;
            max-width: 280px;
          }
          .liveness-link {
            color: #1890ff;
            text-decoration: underline;
            font-size: 13px;
            display: block;
            margin-bottom: 8px;
          }
        `}
      </style>
      <label className="col-6 font-bold">Patient Details</label>
      <div className="row mt-2">
        <div className="col-sm-3">
          {/* <ProfilePictureUpload imageFile={handleSaveImage} /> */}
          <ProfilePictureUpload
            imageFile={handleSaveImage}
            capturedImage={patientPhoto}
          />
          <div style={{ marginTop: "10px" }}>
            <Button
              type="primary"
              icon={<CameraOutlined />}
              onClick={() => setIsCameraModalVisible(true)}
              style={{ width: "100%" }}
            >
              Take Photo
            </Button>
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "4px",
              marginBottom: "10px",
              marginTop: "10px",
            }}
          >
            <input
              type="checkbox"
              id="ph-check"
              style={{ width: "15px", height: "15px" }}
              checked={!!addPatient.ph_check}
              onChange={(e) => {
                setShowPhilHealth(e.target.checked);
                setAddPatient((prevState) => ({
                  ...prevState,
                  ph_check: e.target.checked,
                  phic_membership_type: "member",
                  phic_contact_no: addPatient.contact_no,
                  phic_atc: "NONE",
                  liveness_check_screenshot: e.target.checked ? prevState.liveness_check_screenshot : null,
                }));
                if (!e.target.checked) {
                  setLivenessPreview(null);
                }
              }}
            />
            <label htmlFor="ph-check">Patient is a PhilHealth member</label>
          </div>

          {addPatient.ph_check && (
            <div className="mt-2" style={{ paddingLeft: "20px" }}>
              <a
                href="https://pcu.philhealth.gov.ph/consent"
                target="_blank"
                rel="noopener noreferrer"
                className="liveness-link"
              >
                https://pcu.philhealth.gov.ph/consent
              </a>
              <div className="liveness-note">
                Note: This link is for PhilHealth liveness check. Please perform
                the check and upload the screenshot below.
              </div>
              <div className="liveness-uploader-wrapper">
                <AntDUpload
                  name="liveness_screenshot"
                  listType="picture-card"
                  className="liveness-uploader"
                  showUploadList={false}
                  beforeUpload={() => false}
                  onChange={handleLivenessUpload}
                  accept="image/*"
                >
                  {livenessPreview ? (
                    <img
                      src={livenessPreview}
                      alt="liveness screenshot"
                      style={{ width: "100%" }}
                    />
                  ) : (
                    <div>
                      {livenessLoading ? <LoadingOutlined /> : <PlusOutlined />}
                      <div style={{ marginTop: 8 }}>Upload Screenshot</div>
                    </div>
                  )}
                </AntDUpload>
              </div>
            </div>
          )}
        </div>
        <div className="col-sm-9">
          {/* Row 1: Name Fields */}
          <div className="row">
            <div className="col">
              {type !== "diagnosis-patient" ? (
                <>
                  <CustomTypeAhead
                    required
                    id="last_name"
                    errorMessage="Required"
                    className="dropsearch-filter"
                    name="last_name"
                    size="sm"
                    formLabel="Last Name"
                    options={userData}
                    isMessage
                    onInputChange={(e) => handleAddChange(e, "last")} // This will now receive uppercase text
                    value={addPatient?.last_name?.toUpperCase()}
                    onChange={navigatePatientProfile}
                  />
                </>
              ) : (
                <>
                  <TextField
                    required
                    id="last_name"
                    name="last_name"
                    formLabel="Last Name"
                    size="sm"
                    value={addPatient?.last_name?.toUpperCase()}
                    onChange={handleAddChange}
                    disabled={true}
                  />
                </>
              )}
            </div>
            <div className="col">
              {type !== "diagnosis-patient" ? (
                <>
                  <CustomTypeAhead
                    required
                    id="first_name"
                    errorMessage="Required"
                    className="dropsearch-filter"
                    name="first_name"
                    size="sm"
                    formLabel="First Name"
                    options={userData}
                    isMessage
                    onInputChange={(e) => handleAddChange(e, "first")}
                    value={addPatient?.first_name?.toUpperCase()}
                    onChange={navigatePatientProfile}
                  />
                </>
              ) : (
                <>
                  <TextField
                    required
                    id="first_name"
                    name="first_name"
                    formLabel="First Name"
                    size="sm"
                    value={addPatient?.first_name?.toUpperCase()}
                    onChange={handleAddChange}
                    disabled={true}
                  />
                </>
              )}
            </div>
            {/* <div className="col">
              <TextField
                required
                id="middle_name"
                name="middle_name"
                formLabel="Middle Name"
                size="sm"
                value={addPatient.middle_name}
                onChange={handleAddChange}
                disabled={type === "diagnosis-patient"}
              />
            </div> */}
            <div className="col">
              <TextField
                required
                id="middle_name"
                name="middle_name"
                formLabel={
                  <div className="flex items-center">
                    <span className="me-3">Middle Name</span>{" "}
                    {/* adds space before checkbox */}
                    <input
                      type="checkbox"
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setIsMiddleNameNA(checked);
                        if (checked) {
                          setAddPatient((prev) => ({
                            ...prev,
                            middle_name: "",
                            has_no_middle_name: true,
                          }));
                        } else {
                          setAddPatient((prev) => ({
                            ...prev,
                            has_no_middle_name: false,
                          }));
                        }
                      }}
                      checked={isMiddleNameNA}
                    />
                    <span className="ms-1">N/A</span>
                  </div>
                }
                size="sm"
                value={addPatient.middle_name}
                onChange={(e) => {
                  if (isMiddleNameNA) return;
                  handleAddChange(e);
                  const nextValue = e?.target?.value;
                  if (typeof nextValue === "string" && nextValue.trim()) {
                    setAddPatient((prev) => ({
                      ...prev,
                      has_no_middle_name: false,
                    }));
                  }
                }}
                disabled={isMiddleNameNA}
              />
            </div>

            <div className="col">
              <TextField
                // required
                id="suffix"
                name="suffix"
                formLabel="Suffix"
                size="sm"
                value={addPatient.suffix}
                onChange={handleAddChange}
                disabled={type === "diagnosis-patient"}
              />
            </div>
          </div>

          <div className="row">
            {/* <div className="col-sm-12">
              <TextField
                required
                id="address"
                name="address"
                formLabel="Address"
                size="sm"
                value={addPatient.address}
                onChange={handleAddChange}
                isError={isError.address}
                errorMessage="Address is required."
                disabled={type === "diagnosis-patient"}
              />
            </div> */}
            <div className="col-sm-4">
              <div className="row">
                <span>
                  Country <label className="red"> *</label>
                </span>
                <Select
                  className="custom-select-height w-full"
                  value={addPatient?.country}
                  placeholder="Select Country"
                  options={countryOptions}
                  size="small"
                  onChange={(value) =>
                    handleAddChange({
                      target: { name: "country", value: value.toUpperCase() },
                    })
                  }
                  showSearch
                />
              </div>
            </div>
            {/* {addPatient.country?.toUpperCase() === "PHILIPPINES" && (
              <>
                <div className="col-sm-3">
                  <TextField
                    required
                    id="region"
                    name="region"
                    formLabel="Region"
                    size="sm"
                    value={addPatient.region}
                    onChange={handleAddChange}
                    isError={isError.region}
                    errorMessage="Region is required."
                    disabled={type === "diagnosis-patient"}
                  />
                </div>
              </>
            )} */}
            {addPatient?.country?.toUpperCase() !== "PHILIPPINES" && (
              <>
                <div className="col-sm-4">
                  <TextField
                    required
                    id="province"
                    name="province"
                    formLabel="State/Province/Region"
                    size="sm"
                    value={addPatient.province}
                    onChange={handleAddChange}
                    isError={isError.province}
                    errorMessage="State/Province/Region is required."
                    disabled={type === "diagnosis-patient"}
                  />
                </div>
              </>
            )}
            {addPatient.country?.toUpperCase() === "PHILIPPINES" && (
              <>
                <div className="col-sm-4">
                  <div className="row">
                    <span>
                      Province <label className="red"> *</label>
                    </span>
                    <Select
                      placeholder="SELECT"
                      className="custom-select-height w-full"
                      value={addPatient.province}
                      options={phProvinces.length ? phProvinces : phProvincesList}
                      size="small"
                      onChange={(value) => handleProvinceChange(value)}
                      showSearch
                    />
                  </div>
                </div>
              </>
            )}
            {addPatient?.country?.toUpperCase() !== "PHILIPPINES" && (
              <>
                <div className="col-sm-4">
                  <TextField
                    required
                    id="city"
                    name="city"
                    formLabel="City/Town"
                    size="sm"
                    value={addPatient.city}
                    onChange={handleAddChange}
                    isError={isError.city}
                    errorMessage="City/Town is required."
                    disabled={type === "diagnosis-patient"}
                  />
                </div>
              </>
            )}
            {addPatient.country?.toUpperCase() === "PHILIPPINES" && (
              <>
                <div className="col-sm-4">
                  <div className="row">
                    <span>
                      City <label className="red"> *</label>
                    </span>
                    <Select
                      placeholder="SELECT"
                      className="custom-select-height w-full"
                      value={addPatient.city}
                      options={phCities.length ? phCities : phCitiesList}
                      size="small"
                      onChange={(value) => handleCityChange(value)}
                      showSearch
                    />
                  </div>
                </div>
              </>
            )}
          </div>
          <div className="row">
            {addPatient.country?.toUpperCase() === "PHILIPPINES" && (
              <>
                <div className="col-sm-4 mt-4">
                  <TextField
                    required
                    id="barangay"
                    name="barangay"
                    formLabel="Barangay"
                    size="sm"
                    value={addPatient.barangay}
                    onChange={handleBarangayInputChange}
                    isError={isError.barangay}
                    errorMessage="Barangay is required."
                    disabled={type === "diagnosis-patient"}
                  />
                </div>
              </>
            )}
            {addPatient.country?.toUpperCase() === "PHILIPPINES" && (
              <>
                <div className="col-sm-5 mt-4">
                  <TextField
                    required
                    id="street"
                    name="street"
                    formLabel="Street"
                    size="sm"
                    value={addPatient.street}
                    onChange={handleAddChange}
                    isError={isError.Street}
                    errorMessage="street is required."
                    disabled={type === "diagnosis-patient"}
                  />
                </div>
              </>
            )}
            {addPatient?.country?.toUpperCase() !== "PHILIPPINES" && (
              <>
                <div className="col-sm-9">
                  <TextField
                    required
                    id="street"
                    name="street"
                    formLabel="Street"
                    size="sm"
                    value={addPatient.street}
                    onChange={handleAddChange}
                    isError={isError.Street}
                    errorMessage="street is required."
                    disabled={type === "diagnosis-patient"}
                  />
                </div>
              </>
            )}
            <div
              className={
                addPatient?.country?.toUpperCase() === "PHILIPPINES"
                  ? "col-sm-3 mt-4"
                  : "col-sm-3"
              }
            >
              <TextField
                required
                id="zip_code"
                name="zip_code"
                formLabel="Zip Code"
                size="sm"
                value={addPatient.zip_code}
                onChange={handleAddChange}
                isError={isError.zip_code}
                errorMessage="Zip Code is required."
                disabled={type === "diagnosis-patient"}
              />
            </div>
          </div>
          <div className="row">
            <div className="col-sm-4">
              <DateField
                required
                id="birthdate"
                name="birthdate"
                formLabel="Birthdate"
                size="sm"
                value={addPatient.birthdate}
                onChange={(e) => {
                  const next = e?.target?.value;
                  if (next && next > todayISO) {
                    setAddPatient((prev) => ({
                      ...prev,
                      birthdate: todayISO,
                    }));
                    setBirthdateFutureError(true);
                    return;
                  }
                  setBirthdateFutureError(false);
                  handleAddChange(e);
                }}
                max={todayISO}
                isError={birthdateFutureError || isError.birthdate}
                errorMessage={
                  birthdateFutureError
                    ? "Birthdate cannot be later than today."
                    : isError.birthdate
                    ? "Birthdate is required."
                    : "Birthdate is required."
                }
                disabled={type === "diagnosis-patient"}
              />
            </div>
            <div className="col-sm-4">
              <div className="row">
                <span>
                  Sex <label className="red"> *</label>
                </span>
                <Select
                  placeholder="SELECT"
                  className="custom-select-height w-full"
                  value={addPatient.sex}
                  options={[
                    { value: "male", label: "MALE" },
                    { value: "female", label: "FEMALE" },
                  ]}
                  size="small"
                  onChange={(value) =>
                    handleAddChange({
                      target: { name: "sex", value },
                    })
                  }
                  disabled={type === "diagnosis-patient"}
                />
              </div>
            </div>
            <div className="col-sm-4">
              <div className="row">
                <span>
                  Status <label className="red"> *</label>
                </span>
                <Select
                  placeholder="SELECT"
                  className="custom-select-height w-full"
                  value={addPatient.civil_status}
                  options={[
                    { value: "child", label: "CHILD" },
                    { value: "single", label: "SINGLE" },
                    { value: "married", label: "MARRIED" },
                    { value: "divorced", label: "DIVORCED" },
                    { value: "separated", label: "SEPARATED" },
                    { value: "widowed", label: "WIDOWED" },
                  ]}
                  size="small"
                  onChange={(value) =>
                    handleAddChange({
                      target: { name: "civil_status", value },
                    })
                  }
                  disabled={type === "diagnosis-patient"}
                />
              </div>
            </div>
          </div>

          <div className="row">
            {/* <div className="col">
              <TextField
                required
                id="phone_no"
                name="phone_no"
                formLabel="Phone Number"
                size="sm"
                value={addPatient.phone_no}
                onChange={handleAddChange}
                disabled={type === "diagnosis-patient"}
              />
            </div> */}
            <div className="col-sm-4">
              <span>
                Mobile Number <label className="red"> *</label>
              </span>
              <PhoneInput
                id="contact_no"
                name="contact_no"
                defaultCountry="ph"
                value={addPatient.contact_no}
                // onChange={(value) => {
                //   // Check if the value is just the country code or empty
                //   const phoneValue = value.replace(/\D/g, ""); // Remove non-digits
                //   if (phoneValue.length <= 3) {
                //     // Country code only
                //     handleAddChange({
                //       target: { name: "contact_no", value: "" },
                //     });
                //   } else {
                //     handleAddChange({
                //       target: { name: "contact_no", value },
                //     });
                //   }
                // }}
                onChange={(e) => handleAddPhoneChange(e, "contact_no")}
                disabled={type === "diagnosis-patient"}
                style={{
                  width: "100%",
                  height: "32px",
                }}
                inputStyle={{
                  width: "100%",
                  height: "32px",
                  border: "1px solid #d9d9d9",
                }}
                countrySelectorStyleProps={{
                  style: {
                    height: "32px",
                  },
                  buttonStyle: {
                    height: "32px",
                    padding: "0 8px",
                  },
                }}
              />
            </div>
            <div className="col-sm-4">
              <TextField
                id="occupation"
                name="occupation"
                formLabel="Occupation"
                size="sm"
                value={addPatient.occupation}
                onChange={handleAddChange}
                disabled={loading}
              />
            </div>
            {/* <div className="col-sm-4">
              <TextField
                id="nationality"
                name="nationality"
                formLabel="Nationality"
                size="sm"
                onChange={handleAddChange}
                disabled={loading}
              />
            </div> */}
            <div className="col-sm-4">
              <div className="row">
                <span>Nationality</span>
                <Select
                  className="custom-select-height w-full"
                  value={addPatient.nationality}
                  placeholder="Select Nationality"
                  options={nationalities}
                  size="small"
                  onChange={(value) =>
                    handleAddChange({
                      target: { name: "nationality", value },
                    })
                  }
                  showSearch
                />
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-sm-4">
              <TextField
                id="religion"
                name="religion"
                formLabel="Religion"
                size="sm"
                value={addPatient.religion}
                onChange={handleAddChange}
                disabled={loading}
              />
            </div>
            <div className="col-sm-4">
              <TextField
                id="email"
                name="email"
                formLabel="Email"
                size="sm"
                onChange={handleAddChange}
                value={addPatient.email}
                disabled={loading}
              />
            </div>
            <div className="col-sm-4">
              <TextField
                required={
                  getAge(addPatient.birthdate) < 18 ||
                  needsSeniorPwdEmergencyContact
                }
                id="emergency_contact"
                name="emergency_contact"
                formLabel={"Emergency Contact Person"}
                size="sm"
                onChange={handleAddChange}
                value={addPatient.emergency_contact}
                disabled={loading}
              />
            </div>
          </div>
          <div className="row">
            <div className="col-sm-4">
              <TextField
                required={
                  getAge(addPatient.birthdate) < 18 ||
                  needsSeniorPwdEmergencyContact
                }
                id="relation_w_contact"
                name="relation_w_contact"
                formLabel={"Relationship"}
                size="sm"
                onChange={handleAddChange}
                value={addPatient.relation_w_contact}
                disabled={loading}
              />
            </div>
            <div className="col-sm-4">
              {/* <TextField
                id="em_phone_no"
                name="em_phone_no"
                formLabel="Emergency Contact Number"
                size="sm"
                onChange={handleAddChange}
                disabled={loading}
              /> */}
              <span>
                Emergency Contact Number{" "}
                {(getAge(addPatient.birthdate) < 18 ||
                  needsSeniorPwdEmergencyContact) && (
                  <label className="red">*</label>
                )}
              </span>
              <PhoneInput
                id="emergency_contact_no"
                name="emergency_contact_no"
                defaultCountry="ph"
                value={addPatient.emergency_contact_no}
                onChange={(e) =>
                  handleAddPhoneChange(e, "emergency_contact_no")
                }
                disabled={type === "diagnosis-patient"}
                style={{
                  width: "100%",
                  height: "32px",
                }}
                inputStyle={{
                  width: "100%",
                  height: "32px",
                  border: "1px solid #d9d9d9",
                }}
                countrySelectorStyleProps={{
                  style: {
                    height: "32px",
                  },
                  buttonStyle: {
                    height: "32px",
                    padding: "0 8px",
                  },
                }}
              />
            </div>
            <div className="col-sm-4">
              <TextField
                id="senior_id"
                name="senior_id"
                formLabel="Senior ID"
                size="sm"
                value={addPatient.senior_id}
                onChange={handleAddChange}
                disabled={!(getAge(addPatient.birthdate) >= 60)}
              />
            </div>
            <div className="col-sm-4">
              <p className="form-label align-left">
                is PWD <span className="badge">*</span>
              </p>
              <Radio.Group
                className="w-full"
                name="is_pwd"
                value={addPatient.is_pwd}
                onChange={handleAddChange}
              >
                <Radio value="1">Yes</Radio>
                <Radio value="0">No</Radio>
              </Radio.Group>
            </div>
            {/* {addPatient.is_pwd === "1" && (
              <> */}
            <div className="col-sm-4">
              <TextField
                id="pwd_id"
                name="pwd_id"
                formLabel="PWD ID"
                size="sm"
                value={addPatient.pwd_id}
                onChange={handleAddChange}
                disabled={addPatient.is_pwd === "0"}
              />
            </div>
            {/* </>
            )} */}
          </div>

          {/* <div className="row">
            <div className="col">
              <TextField
                id="referred_by"
                name="referred_by"
                formLabel="Referred By"
                size="sm"
                onChange={handleAddChange}
                disabled={loading}
              />
            </div>
          </div> */}

          {/* <div className="row">
            <div className="col-sm-3">
              <TextField
                id="hmo_type"
                name="hmo_type"
                formLabel={`HMO Type`}
                size={`sm`}
                onChange={handleAddChange}
                disabled={
                  loading ||
                  addPatient.corporate_type ||
                  addPatient.corporate_id
                }
              />
            </div>
            <div className="col-sm-3">
              <TextField
                id="hmo_id"
                name="hmo_id"
                formLabel={`HMO ID`}
                size={`sm`}
                onChange={handleAddChange}
                disabled={
                  loading ||
                  addPatient.corporate_type ||
                  addPatient.corporate_id
                }
              />
            </div>
            <div className="col-sm-3">
              <TextField
                id="corporate_type"
                name="corporate_type"
                formLabel={`Corporate Type`}
                size={`sm`}
                onChange={handleAddChange}
                disabled={loading || addPatient.hmo_type || addPatient.hmo_id}
              />
            </div>
            <div className="col-sm-3">
              <TextField
                id="corporate_id"
                name="corporate_id"
                formLabel={`Corporate ID`}
                size={`sm`}
                onChange={handleAddChange}
                disabled={loading || addPatient.hmo_type || addPatient.hmo_id}
              />
            </div>
          </div> */}
        </div>
        {/* PhilHealth registration fields */}
        {showPhilHealth && (
          <div className="form-container">
            <label className="col-6 font-bold mt-3 mb-2">
              PhilHealth Membership Details
            </label>
            <div className="row">
              <div className="col">
                <div className="row">
                  <span>Membership Type</span>
                  <Select
                    className="custom-select-height w-full"
                    value={addPatient?.phic_membership_type || "member"}
                    placeholder="Select membership type"
                    options={[
                      {
                        value: "member",
                        label: "Patient is a PhilHealth member",
                      },
                      {
                        value: "dependent",
                        label: "Patient is a dependent of a PhilHealth member",
                      },
                    ]}
                    size="small"
                    onChange={(value) =>
                      handleAddChange({
                        target: { name: "phic_membership_type", value },
                      })
                    }
                  />
                </div>
              </div>
              <div className="col">
                <TextField
                  required
                  id="phic_pin"
                  name="phic_pin"
                  formLabel={`Member PhilHealth Identification Number (PIN)`}
                  size={`sm`}
                  value={addPatient?.phic_pin}
                  onChange={handleAddChange}
                  isError={isError.phic_pin}
                  errorMessage="Member PIN is required."
                  disabled={loading}
                  maxLength={12}
                />
              </div>
              <div className="col">
                {addPatient.phic_membership_type === "dependent" && (
                  <>
                    <TextField
                      required={
                        addPatient.phic_membership_type === "dependent"
                          ? true
                          : false
                      }
                      id="phic_dependent_pin"
                      name="phic_dependent_pin"
                      formLabel={`Dependent PhilHealth Identification Number (PIN)`}
                      size={`sm`}
                      value={addPatient?.phic_dependent_pin}
                      onChange={handleAddChange}
                      isError={isError.phic_dependent_pin}
                      errorMessage="Dependent PIN is required."
                      disabled={loading}
                      maxLength={12}
                    />
                  </>
                )}
              </div>
            </div>
            <div className="row">
              <div className="col">
                <TextField
                  required
                  id="phic_last_name"
                  name="phic_last_name"
                  formLabel={`Last Name`}
                  size={`sm`}
                  value={
                    addPatient.phic_membership_type === "member"
                      ? handleValueChange(
                          "phic_last_name",
                          addPatient.last_name
                        )
                      : addPatient.phic_last_name
                  }
                  onChange={handleAddChange}
                  isError={isError.last_name}
                  errorMessage="Last name is required."
                  disabled={
                    addPatient.phic_membership_type === "member" || loading
                  }
                />
              </div>
              <div className="col">
                <TextField
                  required
                  id="phic_first_name"
                  name="phic_first_name"
                  formLabel={`First Name`}
                  size={`sm`}
                  value={
                    addPatient.phic_membership_type === "member"
                      ? handleValueChange(
                          "phic_first_name",
                          addPatient.first_name
                        )
                      : addPatient.phic_first_name
                  }
                  onChange={handleAddChange}
                  isError={isError.first_name}
                  errorMessage="First name is required."
                  disabled={
                    addPatient.phic_membership_type === "member" || loading
                  }
                />
              </div>
              <div className="col">
                <TextField
                  id="phic_middle_name"
                  name="phic_middle_name"
                  formLabel={`Middle Name`}
                  size={`sm`}
                  value={
                    addPatient.phic_membership_type === "member"
                      ? handleValueChange(
                          "phic_middle_name",
                          addPatient.middle_name
                        )
                      : addPatient.phic_middle_name
                  }
                  onChange={handleAddChange}
                  isError={isError.phic_middle_name}
                  errorMessage="Middle name is required."
                  disabled={
                    addPatient.phic_membership_type === "member" || loading
                  }
                />
              </div>
              <div className="col">
                <TextField
                  id="phic_extension_name"
                  name="phic_extension_name"
                  formLabel={`Extension Name`}
                  size={`sm`}
                  value={
                    addPatient.phic_membership_type === "member"
                      ? handleValueChange(
                          "phic_extension_name",
                          addPatient.suffix
                        )
                      : addPatient.phic_extension_name
                  }
                  onChange={handleAddChange}
                  isError={isError.phic_extension_name}
                  disabled={
                    addPatient.phic_membership_type === "member" || loading
                  }
                />
              </div>
            </div>
            <div className="row">
              <div className="col">
                <DateField
                  required
                  id="phic_birthdate"
                  name="phic_birthdate"
                  formLabel={`Birth date of primary member`}
                  size={`sm`}
                  value={
                    addPatient.phic_membership_type === "member"
                      ? handleValueChange(
                          "phic_birthdate",
                          addPatient.birthdate
                        )
                      : addPatient.phic_birthdate
                  }
                  onChange={handleAddChange}
                  isError={isError.phic_birthdate}
                  errorMessage="Birth date of primary member is required."
                  disabled={
                    addPatient.phic_membership_type === "member" || loading
                  }
                />
              </div>
              {/* <div className="col">
                <TextField
                  required
                  id="phic_contact_no"
                  name="phic_contact_no"
                  formLabel={`Mobile number`}
                  size={`sm`}
                  value={
                    addPatient.phic_membership_type === "member"
                      ? handleValueChange(
                          "phic_contact_no",
                          addPatient.phone_no
                        )
                      : addPatient.phic_contact_no
                  }
                  onChange={handleAddChange}
                  isError={isError.phic_contact_no}
                  errorMessage="Mobile number is required."
                  disabled={
                    addPatient.phic_membership_type === "member" || loading
                  }
                />
              </div> */}
              <div className="col-sm-4">
                <span>
                  Mobile Number <label className="red"> *</label>
                </span>
                <PhoneInput
                  id="phic_contact_no"
                  name="phic_contact_no"
                  defaultCountry="ph"
                  value={addPatient?.phic_contact_no}
                  onChange={(e) => handleAddPhoneChange(e, "phic_contact_no")}
                  disabled={
                    addPatient.phic_membership_type === "member" || loading
                  }
                  style={{
                    width: "100%",
                    height: "32px",
                  }}
                  inputStyle={{
                    width: "100%",
                    height: "32px",
                    border: "1px solid #d9d9d9",
                  }}
                  countrySelectorStyleProps={{
                    style: {
                      height: "32px",
                    },
                    buttonStyle: {
                      height: "32px",
                      padding: "0 8px",
                    },
                  }}
                />
              </div>
              <div className="col">
                <DateField
                  required
                  id="phic_enlistment_date"
                  name="phic_enlistment_date"
                  formLabel={`Enlistment date`}
                  size={`sm`}
                  onChange={handleAddChange}
                  isError={isError.phic_enlistment_date}
                  errorMessage="Enlistment date is required."
                  disabled={loading}
                />
              </div>
            </div>
            <div className="row">
              {/* <div className="col">
                <SelectField
                  required
                  formLabel={`Enlistment status`}
                  name="ph_enlistment_status"
                  value={addPatient.ph_enlistment_status}
                  size={`sm`}
                  placeholder={"Select enlistment status"}
                  options={[
                    { value: "active", label: "Active" },
                    { value: "cancelled", label: "Cancelled" },
                    { value: "transferred", label: "Transferred" },
                  ]}
                  onChange={handleAddChange}
                  isError={isError.ph_enlistment_status}
                  errorMessage="Enlistment status is required."
                  disabled={loading}
                />
              </div> */}
              <div className="col">
                <div className="row">
                  <span>Enlistment Status</span>
                  <Select
                    className="custom-select-height w-full"
                    placeholder={"Select enlistment status"}
                    options={[
                      { value: "active", label: "Active" },
                      { value: "cancelled", label: "Cancelled" },
                      { value: "transferred", label: "Transferred" },
                    ]}
                    size="small"
                    value={addPatient.phic_enlistment_status}
                    onChange={(value) =>
                      handleAddChange({
                        target: { name: "phic_enlistment_status", value },
                      })
                    }
                  />
                </div>
              </div>
              {/* <div className="col">
                <SelectField
                  required
                  formLabel={`Package type`}
                  name="ph_package_type"
                  value={addPatient.package_type}
                  size={`sm`}
                  placeholder={"Select package type"}
                  options={[
                    { value: "Konsulta Package", label: "KONSULTA Package" },
                    { value: "PCB1", label: "PCB1" },
                    { value: "Expanded PCB", label: "Expanded PCB" },
                  ]}
                  onChange={handleAddChange}
                  isError={isError.ph_package_type}
                  errorMessage="Enlistment status is required."
                  disabled={loading}
                />
              </div> */}
              <div className="col">
                <div className="row">
                  <span>Package Type</span>
                  <Select
                    className="custom-select-height w-full"
                    placeholder={"Select package type"}
                    options={[
                      { value: "Konsulta Package", label: "KONSULTA Package" },
                      { value: "PCB1", label: "PCB1" },
                      { value: "Expanded PCB", label: "Expanded PCB" },
                    ]}
                    size="small"
                    value={addPatient.phic_package_type}
                    onChange={(value) =>
                      handleAddChange({
                        target: { name: "phic_package_type", value },
                      })
                    }
                  />
                </div>
              </div>
              <div className="col">
                <TextField
                  required
                  id="phic_atc"
                  name="phic_atc"
                  formLabel={`Authorization Transaction Code (ATC)`}
                  size={`sm`}
                  onChange={handleAddChange}
                  value={addPatient?.phic_atc}
                  isError={isError.phic_atc}
                  errorMessage="Authorization Transaction Code (ATC) is required."
                  disabled={loading}
                />
              </div>
            </div>
          </div>
        )}
      </div>
      {/* Camera Modal */}
      <Modal
        title="Take Patient Photo"
        open={isCameraModalVisible}
        onCancel={() => {
          setIsCameraModalVisible(false);
          setCapturedImage(null);
        }}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setIsCameraModalVisible(false);
              setCapturedImage(null);
            }}
          >
            Cancel
          </Button>,
          capturedImage ? (
            <Button key="retake" onClick={retakePhoto}>
              Retake
            </Button>
          ) : (
            <Button key="capture" type="primary" onClick={capturePhoto}>
              Capture Photo
            </Button>
          ),
          capturedImage && (
            <Button key="use" type="primary" onClick={usePhoto}>
              Use This Photo
            </Button>
          ),
        ]}
        width={700}
      >
        {/* Camera Selection Dropdown */}
        {availableCameras.length > 1 && (
          <div style={{ marginBottom: "16px" }}>
            <Select
              style={{ width: "100%" }}
              value={selectedCamera}
              onChange={handleCameraChange}
              suffixIcon={<SettingOutlined />}
              placeholder="Select Camera"
            >
              {availableCameras.map((camera) => (
                <Select.Option key={camera.deviceId} value={camera.deviceId}>
                  {camera.label ||
                    `Camera ${availableCameras.indexOf(camera) + 1}`}
                </Select.Option>
              ))}
            </Select>
          </div>
        )}

        <div style={{ textAlign: "center" }}>
          {!capturedImage ? (
            <Webcam
              audio={false}
              ref={webcamRef}
              screenshotFormat="image/jpeg"
              videoConstraints={videoConstraints}
              style={{
                width: "100%",
                maxHeight: "500px",
                border: "2px dashed #d9d9d9",
                borderRadius: "8px",
              }}
              onUserMediaError={(error) => {
                console.error("Webcam error:", error);
                // Fallback to default camera if selected camera fails
                if (availableCameras.length > 0) {
                  setSelectedCamera(availableCameras[0].deviceId);
                }
              }}
            />
          ) : (
            <img
              src={capturedImage}
              alt="Captured"
              style={{
                width: "100%",
                maxHeight: "500px",
                objectFit: "contain",
                border: "2px solid #1890ff",
                borderRadius: "8px",
              }}
            />
          )}
        </div>

        {/* Camera instructions */}
        {availableCameras.length === 0 && (
          <div
            style={{
              padding: "16px",
              backgroundColor: "#fff2e8",
              borderRadius: "4px",
              marginTop: "16px",
              textAlign: "center",
            }}
          >
            <p>
              No cameras detected. Please ensure your webcam is connected and
              permissions are granted.
            </p>
          </div>
        )}
      </Modal>
    </>
  );
};

export default AddPatientForm;
